import { useRef, useEffect } from 'react';
import { SESSION_ACTIVITY_EVENT } from '../utils/sessionActivity';

const SESSION_TIMEOUT_MS = 45 * 60 * 1000; // 45 minutes
const SESSION_WARNING_MS = 40 * 60 * 1000; // Warn at 40 minutes

/**
 * Auto-logs out the user after SESSION_TIMEOUT_MS of inactivity.
 * @param {object} user - Firebase auth user (null when logged out)
 * @param {function} onTimeout - Called when the session expires
 * @param {function} onWarning - Called when 5 minutes remain (pass toast or similar)
 */
const useSessionTimeout = (user, onTimeout, onWarning) => {
  const lastActivityRef = useRef(Date.now());
  const lastCheckRef = useRef(Date.now());
  const warningShownRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      warningShownRef.current = false;
    };

    // `scroll` doesn't bubble, so a plain listener misses scrolling inside a
    // nested overflow:auto container (e.g. a modal body) — { capture: true }
    // catches it anyway, since capturing listeners see an event on its way
    // down to the target regardless of whether it later bubbles back up.
    // `wheel`/`mousemove` are added as a wider net for the same reason.
    // `SESSION_ACTIVITY_EVENT` is a synthetic event any component can fire
    // (see utils/sessionActivity.js) to report activity that never reaches
    // `window` as a real DOM event at all — namely, a document preview
    // rendered in a cross-origin <iframe>, whose internal scroll/click
    // events belong to a different document and can't be observed here.
    const events = ['mousedown', 'keydown', 'scroll', 'wheel', 'mousemove', 'touchstart', 'click', SESSION_ACTIVITY_EVENT];
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true, capture: true }));

    // Reset activity on tab wake — if device slept, treat wake as fresh activity
    const handleVisibility = () => {
      if (!document.hidden) {
        const timeSinceLastCheck = Date.now() - lastCheckRef.current;
        if (timeSinceLastCheck > 60000) {
          lastActivityRef.current = Date.now();
          warningShownRef.current = false;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const interval = setInterval(() => {
      lastCheckRef.current = Date.now();
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= SESSION_TIMEOUT_MS) {
        clearInterval(interval);
        events.forEach(e => window.removeEventListener(e, updateActivity, { capture: true }));
        document.removeEventListener('visibilitychange', handleVisibility);
        onTimeout();
      } else if (idle >= SESSION_WARNING_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        if (onWarning) {
          onWarning('Your session will expire in 5 minutes due to inactivity. Move your mouse or press a key to stay logged in.', 'warning');
        }
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, updateActivity, { capture: true }));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, onTimeout, onWarning]);
};

export default useSessionTimeout;
